'use client';

import { Box, VStack, HStack, token } from '@terra/ui-styled-system';
import { Text, Button, Input, LegacyDialog } from '@terra/ui-design-system';
import { useState, useCallback } from 'react';
import { Cancel01Icon } from '@terra/ui-icons';
import {
  useComparerStore,
  type ComparerItemType,
} from '../../store/comparer-store';
import { IconButton } from '@terra/ui-design-system';

type PasteDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

export function PasteDialog({ isOpen, onClose }: PasteDialogProps) {
  const { addItem } = useComparerStore();
  const [label, setLabel] = useState('');
  const [content, setContent] = useState('');
  const [itemType, setItemType] = useState<ComparerItemType>('text');
  const handlePaste = useCallback(() => {
    if (!content.trim()) return;
    const finalLabel =
      label.trim() || `Pasted ${new Date().toLocaleTimeString()}`;

    addItem({
      id: `pasted-${Date.now()}`,
      label: finalLabel,
      content: content,
      type: itemType,
    });
    // Reset form
    setLabel('');
    setContent('');
    setItemType('text');
    onClose();
  }, [content, label, itemType, addItem, onClose]);
  const handlePasteFromClipboard = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();

      setContent(text);
      // Try to detect type from content
      if (text.startsWith('HTTP/') || text.match(/^\d{3}\s/)) {
        setItemType('response');
      } else if (text.match(/^(GET|POST|PUT|DELETE|PATCH|HEAD|OPTIONS)\s/)) {
        setItemType('request');
      }
    } catch (error) {
      console.error('Failed to read clipboard:', error);
    }
  }, []);
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [],
  );

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="600px">
          <VStack gap="4" p="4">
            {/* Header */}
            <VStack gap="1" alignItems="flex-start" w="full">
              <LegacyDialog.Title>Paste Content to Compare</LegacyDialog.Title>
              <LegacyDialog.Description>
                Paste HTTP request, response, or any text to add to the
                comparer.
              </LegacyDialog.Description>
            </VStack>

            <LegacyDialog.CloseTrigger
              asChild
              position="absolute"
              top="2"
              right="2"
            >
              <IconButton aria-label="Close Dialog" variant="ghost" size="xs">
                <Cancel01Icon size={14} />
              </IconButton>
            </LegacyDialog.CloseTrigger>

            {/* Label input */}
            <Box w="full">
              <Text fontSize="xs" fontWeight="medium" color="fg.default" mb="1">
                Label (optional)
              </Text>
              <Input
                size="sm"
                placeholder="e.g., Original Request, Modified Response"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </Box>

            {/* Type selector */}
            <Box w="full">
              <Text fontSize="xs" fontWeight="medium" color="fg.default" mb="1">
                Type
              </Text>
              <HStack gap="2">
                <Button
                  size="xs"
                  variant={itemType === 'request' ? 'solid' : 'outline'}
                  onClick={() => setItemType('request')}
                >
                  Request
                </Button>
                <Button
                  size="xs"
                  variant={itemType === 'response' ? 'solid' : 'outline'}
                  onClick={() => setItemType('response')}
                >
                  Response
                </Button>
                <Button
                  size="xs"
                  variant={itemType === 'text' ? 'solid' : 'outline'}
                  onClick={() => setItemType('text')}
                >
                  Text
                </Button>
              </HStack>
            </Box>

            {/* Content textarea */}
            <Box w="full">
              <HStack justifyContent="space-between" alignItems="center" mb="1">
                <Text fontSize="xs" fontWeight="medium" color="fg.default">
                  Content
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={handlePasteFromClipboard}
                >
                  Paste from Clipboard
                </Button>
              </HStack>
              <textarea
                style={{
                  width: '100%',
                  height: '200px',
                  padding: '8px',
                  border: '1px solid var(--colors-border-default)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--colors-bg-default)',
                  fontFamily: token('fonts.mono'),
                  fontSize: '12px',
                  resize: 'vertical',
                }}
                value={content}
                onChange={handleContentChange}
                placeholder="Paste your HTTP request, response, or text here..."
              />
            </Box>

            {/* Footer actions */}
            <HStack gap="2" justifyContent="flex-end" w="full">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="solid"
                onClick={handlePaste}
                disabled={!content.trim()}
              >
                Add to Comparer
              </Button>
            </HStack>
          </VStack>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
